import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { PostLightboxImageProps } from "@/types/modules/post/components";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { ImageWithSkeleton } from "@/components/ui-kit/ImageWithSkeleton";
import { Lightbox } from "@/components/ui-kit/Lightbox";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import { useRouter } from "next-translate-routes";
import { useOfflineImageContent } from "@/hooks/indexeddb/useOfflineImageContent";

export const PostLightboxImage: React.FC<PostLightboxImageProps> = ({
  imageSource,
  alt,
  allImageSources,
  priority,
  disableImgLazyLoading,
}) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [offlineThumbnail, setOfflineThumbnail] = useState<string>();
  const isMobile = useMediaQuery(561);
  const scrollPositionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const isGalleryPage = router.asPath.startsWith("/galerie/");
  const { loadOfflineImage } = useOfflineImageContent();

  useEffect(() => {
    if (imageSource) {
      loadOfflineImage(imageSource).then((offlinePostThumbnail) => {
        if (offlinePostThumbnail) {
          setOfflineThumbnail(offlinePostThumbnail);
        }
      });
    }
  }, [imageSource, loadOfflineImage]);

  useEffect(() => {
    if (isMobile && scrollPositionRef.current) {
      const { clientWidth, clientHeight } = scrollPositionRef.current;
      scrollPositionRef.current.scrollTop = clientHeight / 2;
      scrollPositionRef.current.scrollLeft = clientWidth / 2;
    }
    return;
  }, [isLightboxOpen, isMobile]);

  const handleLightBoxOpen = () => {
    setIsLightboxOpen(!isLightboxOpen);
    // article_interaction (open_image / gallery)
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: `${isGalleryPage ? "gallery" : "open_image"}`,
      media_url: imageSource,
    });
  };

  return (
    <>
      <div
        onClick={() => handleLightBoxOpen()}
        className={`${
          !isLightboxOpen && "touch-pan-y"
        } gallery-main-post-img aspect-auto relative block w-full cursor-pointer overflow-auto outline-none`}
        ref={scrollPositionRef}
      >
        {/* This component is required for mobile "zoom-in" */}
        {isMobile ? (
          <>
            <Image
              src={offlineThumbnail ?? imageSource}
              alt={alt || ""}
              width={800}
              height={0}
              quality={95}
              priority={priority ?? false}
              sizes="768px"
              placeholder="blur"
              blurDataURL={offlineThumbnail ?? imageSource}
              className="min-h-[236px] w-full min-w-full object-cover md:hidden"
              loading={disableImgLazyLoading ? "eager" : "lazy"}
            />
            <Image
              src={offlineThumbnail ?? imageSource}
              alt={alt || ""}
              width={800}
              height={0}
              quality={95}
              priority={priority ?? false}
              sizes="768px"
              placeholder="blur"
              blurDataURL={offlineThumbnail ?? imageSource}
              className="!absolute inset-0 min-h-[236px] !w-auto min-w-full object-cover md:hidden"
              loading={disableImgLazyLoading ? "eager" : "lazy"}
              style={{
                maxWidth: isLightboxOpen ? "none" : "100%",
              }}
            />
          </>
        ) : (
          <ImageWithSkeleton
            src={offlineThumbnail ?? imageSource}
            alt={alt || ""}
            width={800}
            height={480}
            quality={95}
            priority={priority ?? false}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            placeholder="blur"
            blurDataURL={offlineThumbnail ?? imageSource}
            className="hidden min-h-[534px] w-full max-w-full object-cover md:block"
            loading={disableImgLazyLoading ? "eager" : "lazy"}
          />
        )}
        <div className="zoom-in-button absolute bottom-8 left-[50%] hidden translate-x-[-50%] items-center justify-between rounded-full bg-dark-70 px-4 py-2.5 text-center md:inline-flex">
          <Image
            src="/assets/images/icons/zoom-in.svg"
            alt="Zoom in image"
            width={18}
            height={18}
            style={{
              width: "18px",
              height: "18px",
            }}
          />
          <span className="ml-2 font-condensed text-sm text-white">Zvětšit</span>
        </div>
      </div>
      {!isMobile && (
        <Lightbox
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          images={allImageSources}
          initialImageIndex={
            allImageSources?.findIndex((image) => {
              return image.src === imageSource;
            }) ?? 0
          }
        />
      )}
    </>
  );
};
